import Phaser from 'phaser'
import { BoardView } from '@/objects/BoardView'
import { createInitialState, setSelection, clearSelection, advanceStage, tick } from '@/game/engine'
import type { GameState, GameMode } from '@/game/types'
import { timerSpeed, START_TIME } from '@/config/balance'
import { isLargeScreen } from '@/main'
import { AudioManager } from '@/services/AudioManager'
import { ShatterEmitter } from '@/objects/ShatterEmitter'
import { makeButton, makeImageButton } from '@/ui/button'
import { findHint } from '@/game/rules'
import { startGame } from '@/services/ranking'
import { useGameStore } from '@/store/gameStore'
import { getHintItems, useHintItem } from '@/services/hintItems'
import { getItemCount, useShopItem } from '@/services/shop'
import { makeRng } from '@/game/rng'

export class Game extends Phaser.Scene {
  state!: GameState
  board!: BoardView
  private seedCounter = 1
  private timerEvent?: Phaser.Time.TimerEvent
  private shatter!: ShatterEmitter
  private paused = false
  private overlay?: Phaser.GameObjects.Container
  private equip: Record<string, boolean> = {} // 대기실에서 장착한 아이템
  private hammerMode = false
  private itemCountTexts: Record<string, Phaser.GameObjects.Text> = {}
  private playerName?: Phaser.GameObjects.Text // 미니 플레이어 곡명
  private playPauseRedraw?: () => void // 재생/정지 아이콘 다시 그리기
  private currentTrackName = '' // 마퀴 갱신 비교용
  private marqueeTween?: Phaser.Tweens.Tween // 곡명 오른쪽→왼쪽 슬라이드
  private marqueeTimer?: Phaser.Time.TimerEvent // 20초마다 슬라이드 트리거
  private nameLeft = 0
  private nameRight = 0

  constructor() { super('Game') }

  init(data: { mode?: GameMode; equip?: Record<string, boolean> }) {
    this.equip = data.equip ?? {}
    this.state = createInitialState(data.mode ?? 'challenge', isLargeScreen, this.nextSeed())
    if (this.state.mode === 'challenge') {
      // ✨ 포인트 2배만 대기실에서 장착 → 플래그(게임오버에서 적용/소모)
      useGameStore.getState().set({ point2xActive: !!this.equip.point2x })
      // 부활은 게임오버 시 보유 여부로 판단(프롬프트). 시간/셔플/망치는 게임 중 버튼.
    } else {
      useGameStore.getState().set({ point2xActive: false })
    }
  }

  nextSeed() { return (this.seedCounter++ * 2654435761) >>> 0 }

  create() {
    this.paused = false
    this.overlay = undefined
    const { width, height } = this.scale
    this.add.image(width / 2, height / 2, 'bg_game').setDisplaySize(width, height).setDepth(-1)
    const boardPx = Math.min(width, height) * 0.92
    // 챌린지: 보드를 위로 올려 아래에 아이템 행 공간 확보. 연습: 중앙.
    const boardCy = this.state.mode === 'challenge' ? height * 0.42 : height / 2
    this.board = new BoardView(this, width / 2, boardCy, boardPx)
    this.board.layout(this.state)
    this.board.render(this.state)
    if (!this.scene.isActive('UI')) {
      this.scene.launch('UI', { game: this })
    }

    AudioManager.init(this)
    AudioManager.startBgm() // 현재 시각(hour)에 맞는 동물의 숲 BGM
    this.buildMiniPlayer(boardCy, boardPx) // 보드 위 미니 음악 플레이어
    this.shatter = new ShatterEmitter(this)

    // 챌린지 시작 시 서버 서명 토큰 발급(게임오버에서 점수 제출 검증에 사용)
    if (this.state.mode === 'challenge') {
      void startGame().then(t => useGameStore.getState().set({ gameToken: t ?? '' }))
    }

    this.events.on('sfx', (k: string) => AudioManager.play(k))
    this.events.on('cleared', (e: { clearedIds: number[]; gainedScore: number; bonusTime: number }) => {
      AudioManager.play(e.clearedIds.length >= 3 ? 'combo' : 'block_clear')
      // 선택 블록이 많을수록 더 크게 터지는 물방울
      const big = Math.min(1.6, 1 + (e.clearedIds.length - 2) * 0.15)
      for (const id of e.clearedIds) {
        const sp = this.board.spriteOf(id)
        if (sp) this.shatter.burst(sp.x, sp.y, 0xeaf6ff, big)
      }
      AudioManager.play('time_bonus', 0.4)
    })
    this.events.on('stageClear', () => AudioManager.play('stage_clear'))

    // Unlock Web Audio on first interaction (Game scene is alive during play,
    // unlike Boot which starts Lobby immediately and tears down its input)
    this.input.once('pointerdown', () => {
      const soundManager = this.sound as Phaser.Sound.WebAudioSoundManager
      if (typeof soundManager.unlock === 'function') soundManager.unlock()
    })

    // Clean up listeners + timer on shutdown so replay (Lobby→Game→…→Game)
    // doesn't stack duplicate handlers (audio firing 2×, 3×…) or leave a live timer
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.removeAllListeners('sfx')
      this.events.removeAllListeners('cleared')
      this.events.removeAllListeners('stageClear')
      this.events.removeAllListeners('state')
      this.timerEvent?.remove()
    })

    this.startTimer()

    // 일시정지 / 나가기 버튼 (우측 상단, 겹치지 않게 간격 확보)
    const r = this.scale.width * 0.058
    const btnY = this.scale.width * 0.08
    makeImageButton(this, width - r * 3.4, btnY, 'icon_pause', () => this.pauseGame(), r)
    makeImageButton(this, width - r * 1.15, btnY, 'icon_exit', () => this.confirmExit(), r)

    if (this.state.mode === 'practice') {
      // 연습 모드: 무제한 힌트 버튼(하단, 힌트 아이템 이미지). 연습엔 랭킹 사다리가 없어 하단이 비어 있음.
      if (this.textures.exists('item_hint')) makeImageButton(this, width / 2, height - width * 0.13, 'item_hint', () => this.showHint(), width * 0.1)
      else makeButton(this, width / 2, height - width * 0.11, '💡 힌트', () => this.showHint(),
        { width: width * 0.44, height: width * 0.12, fontSize: width * 0.05, color: 0x5ec8a0, pressColor: 0x49ad88 })
    } else {
      // 게임 중 사용 아이템(보유 시): 보드 아래 가로 행. 시간/셔플/망치/힌트/폭탄.
      // 힌트 보유 수 = 무료 획득분(getHintItems) + 구매분(shop 'hint') 합산.
      const rowCount = (key: string) => (key === 'hint' ? getHintItems() + getItemCount('hint') : getItemCount(key))
      const ingameItems: { key: string; use: () => void }[] = [
        { key: 'time', use: () => this.useTimeItem() },
        { key: 'shuffle', use: () => this.useShuffleItem() },
        { key: 'hammer', use: () => this.enterHammerMode() },
        { key: 'hint', use: () => this.useHintItemRow() },
        { key: 'bomb', use: () => this.useBombItem() },
      ]
      const ownedItems = ingameItems.filter(it => rowCount(it.key) > 0)
      if (ownedItems.length > 0) {
        const rowY = boardCy + boardPx / 2 + width * 0.085
        const gap = Math.min(width * 0.2, (width * 0.84) / ownedItems.length)
        const startX = width / 2 - (gap * (ownedItems.length - 1)) / 2
        ownedItems.forEach((it, i) => {
          const bx = startX + i * gap
          const ikey = `item_${it.key}`
          if (this.textures.exists(ikey)) makeImageButton(this, bx, rowY, ikey, it.use, width * 0.075)
          else makeButton(this, bx, rowY, it.key, it.use,
            { width: width * 0.13, height: width * 0.115, fontSize: width * 0.04, color: 0xffb347, pressColor: 0xf59a2e })
          // 보유 수 배지: 아이콘(depth 40)보다 위(depth 50)로 올려 가려지지 않게.
          this.itemCountTexts[it.key] = this.add.text(bx + width * 0.062, rowY - width * 0.062, `${rowCount(it.key)}`, {
            fontFamily: 'Arial', fontSize: `${Math.round(width * 0.028)}px`, color: '#ffffff', fontStyle: 'bold',
            backgroundColor: '#e0556b', padding: { x: 3, y: 1 },
          }).setOrigin(0.5).setDepth(50)
        })
      }
    }

    let dragStart: { x: number; y: number } | null = null
    const selRect = this.add.rectangle(0, 0, 0, 0, 0xffffff, 0.15).setStrokeStyle(2, 0xffffff, 0.6).setVisible(false).setDepth(5)

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (this.state.status !== 'playing' || this.paused) return
      if (this.hammerMode) { this.useHammerAt(p); return } // 망치: 탭한 블록 제거
      dragStart = { x: p.x, y: p.y }
      selRect.setVisible(true)
    })
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!dragStart || this.state.status !== 'playing' || this.paused) return
      const x = Math.min(dragStart.x, p.x), y = Math.min(dragStart.y, p.y)
      const w = Math.abs(p.x - dragStart.x), h = Math.abs(p.y - dragStart.y)
      selRect.setPosition(x + w / 2, y + h / 2).setSize(w, h)
      const ids = this.board.idsInRect({ x, y, w, h }, this.state)
      this.state = setSelection(this.state, ids)
      this.board.render(this.state)
    })
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (!dragStart) return
      const dist = Math.hypot(p.x - dragStart.x, p.y - dragStart.y)
      const isTap = dist < this.scale.width * 0.035
      dragStart = null
      selRect.setVisible(false)
      if (isTap) {
        // 탭(드래그 아님): 선택 취소 + 연습 모드면 사용법 안내 토스트
        this.state = setSelection(this.state, [])
        this.board.render(this.state)
        if (this.state.mode === 'practice') this.showToast('블록을 드래그해서 제거해주세요\n블록의 합이 10이 되도록 하세요')
        return
      }
      this.tryClear()
    })
  }

  private toast?: Phaser.GameObjects.Container
  /** 화면 상단에 잠깐 떴다 사라지는 안내 토스트(중복 시 갱신). */
  private showToast(msg: string) {
    const { width, height } = this.scale
    this.toast?.destroy()
    const t = this.add.text(0, 0, msg, {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.04)}px`, color: '#ffffff', fontStyle: 'bold',
      align: 'center',
    }).setOrigin(0.5)
    const bw = t.width + width * 0.1
    const bh = t.height + width * 0.06
    const g = this.add.graphics()
    g.fillStyle(0x10082a, 0.88); g.fillRoundedRect(-bw / 2, -bh / 2, bw, bh, bh * 0.45)
    g.lineStyle(Math.max(2, width * 0.004), 0xffffff, 0.3); g.strokeRoundedRect(-bw / 2, -bh / 2, bw, bh, bh * 0.45)
    const c = this.add.container(width / 2, height * 0.36, [g, t]).setDepth(60).setAlpha(0)
    this.toast = c
    this.tweens.add({ targets: c, alpha: 1, y: height * 0.32, duration: 180, ease: 'Quad.easeOut' })
    this.tweens.add({
      targets: c, alpha: 0, delay: 1400, duration: 350, ease: 'Quad.easeIn',
      onComplete: () => { if (this.toast === c) { c.destroy(); this.toast = undefined } },
    })
  }

  private tryClear() {
    // clearSelection이 선택 블록을 0으로 만들기 전에 id를 캡처(연출용)
    const selectedBefore = [...this.state.selectedIds]
    const { state, effect } = clearSelection(this.state, this.nextSeed())
    this.state = state
    this.board.render(this.state)
    if (effect.type === 'invalid') {
      this.state = setSelection(this.state, [])
      this.board.render(this.state)
      this.events.emit('sfx', 'invalid')
      return
    }
    if (effect.type === 'cleared') {
      this.events.emit('cleared', effect)
      this.animateCleared(effect.clearedIds)
    }
    if (effect.type === 'stageClear') {
      this.events.emit('stageClear', effect)
      // 타이머는 전환이 끝날 때까지 멈춤(status가 stageCompleting이라 tick은 무시되지만 안전하게 제거)
      this.timerEvent?.remove()
      void this.runStageClearSequence(selectedBefore, effect.remainingIds)
    }
    this.events.emit('state', this.state) // UI 갱신 (Task 9)
  }

  private delay(ms: number): Promise<void> {
    return new Promise(res => this.time.delayedCall(ms, () => res()))
  }

  // 스테이지 클리어: 트리거 블록 터뜨림 → 남은 블록 순서대로 하나씩 터뜨림 → 화면 전환 → 다음 스테이지
  private async runStageClearSequence(triggerIds: number[], remainingIds: number[]) {
    // 1) 합10으로 사라진 트리거 블록들 물방울 효과
    for (const id of triggerIds) {
      const sp = this.board.spriteOf(id)
      if (sp) this.shatter.burst(sp.x, sp.y, 0xeaf6ff, 1.2)
    }
    await this.delay(220)

    // 2) 남아있는 블록을 순서대로 하나씩 터뜨림
    for (const id of remainingIds) {
      const sp = this.board.spriteOf(id)
      if (sp) {
        this.shatter.burst(sp.x, sp.y, 0xeaf6ff, 1.15)
        AudioManager.play('block_clear', 0.55)
        void sp.popOut()
      }
      await this.delay(90)
    }
    await this.delay(220)

    // 3) 화면 전환 연출과 함께 다음 스테이지로
    await this.stageTransition(() => {
      this.state = advanceStage(this.state, this.nextSeed())
      this.board.layout(this.state)
      this.board.render(this.state)
      this.events.emit('state', this.state)
    })

    // 4) 새 스테이지 타이머 시작
    this.startTimer()
  }

  // 카메라 페이드 아웃 → (화면 가려진 동안 onBlack 실행) → STAGE 배너 + 페이드 인
  private stageTransition(onBlack: () => void): Promise<void> {
    return new Promise(res => {
      const cam = this.cameras.main
      const tint: [number, number, number] = [190, 225, 255] // 밝은 하늘색
      cam.fadeOut(300, ...tint)
      cam.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
        onBlack() // 화면이 가려진 동안 보드 교체
        const { width, height } = this.scale
        const banner = this.add
          .text(width / 2, height / 2, `STAGE ${this.state.stage}`, {
            fontFamily: 'Arial',
            fontSize: `${Math.round(width * 0.13)}px`,
            color: '#ffffff',
            fontStyle: 'bold',
            stroke: '#3a2a6a',
            strokeThickness: Math.round(width * 0.018),
          })
          .setOrigin(0.5)
          .setDepth(60)
          .setScale(0)
          .setShadow(0, Math.round(width * 0.012), '#00000066', 10)
        this.tweens.add({ targets: banner, scale: 1, duration: 260, ease: 'Back.easeOut' })
        cam.fadeIn(300, ...tint)
        this.time.delayedCall(520, () => {
          this.tweens.add({
            targets: banner,
            alpha: 0,
            scale: 1.5,
            duration: 240,
            ease: 'Quad.easeIn',
            onComplete: () => banner.destroy(),
          })
          res()
        })
      })
    })
  }

  private startTimer() {
    this.timerEvent?.remove()
    const interval = Math.max(500, 1000 * timerSpeed(this.state.stage))
    let prevTime = this.state.timeRemaining
    this.timerEvent = this.time.addEvent({
      delay: interval,
      loop: true,
      callback: () => {
        if (this.state.status !== 'playing' || this.paused) return
        const timeBefore = prevTime
        this.state = tick(this.state)
        prevTime = this.state.timeRemaining
        this.events.emit('state', this.state)
        if (timeBefore > 10 && this.state.timeRemaining <= 10) this.events.emit('sfx', 'time_warning')
        if (this.state.timeRemaining <= 5 && this.state.timeRemaining > 0) this.events.emit('sfx', 'countdown')
        if (this.state.status === 'gameOver') {
          this.timerEvent?.remove()
          this.onGameOver()
        }
      },
    })
  }

  private onGameOver() {
    // 부활 아이템 보유(챌린지) → 힌트 + 부활 사용 프롬프트. 없으면 바로 종료.
    if (this.state.mode === 'challenge' && getItemCount('revive') > 0) {
      this.showRevivePrompt()
      return
    }
    this.finishGameOver()
  }

  private finishGameOver() {
    this.events.emit('sfx', 'game_over')
    this.scene.stop('UI')
    this.scene.start('GameOver', { score: this.state.score, stage: this.state.stage })
  }

  /** 게임오버 시: 부활 사용 여부만 간단히 묻는다(아이콘 + 보유 수). 부활 시 풀타임 재시작. */
  private showRevivePrompt() {
    const { width, height } = this.scale
    this.state = { ...this.state, selectedIds: [] }
    this.board.render(this.state)

    const cx = width / 2, cy = height * 0.5
    const pw = width * 0.76, ph = width * 0.74
    const c = this.add.container(0, 0).setDepth(120)
    const dim = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.55).setInteractive()
    const g = this.add.graphics()
    g.fillStyle(0x140d2e, 0.97); g.fillRoundedRect(cx - pw / 2, cy - ph / 2, pw, ph, ph * 0.1)
    g.lineStyle(Math.max(2, width * 0.006), 0xff8a8a, 0.75); g.strokeRoundedRect(cx - pw / 2, cy - ph / 2, pw, ph, ph * 0.1)
    const title = this.add.text(cx, cy - ph * 0.35, '부활 하시겠습니까?', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.052)}px`, color: '#ffffff', fontStyle: 'bold',
      stroke: '#3a2a6a', strokeThickness: Math.round(width * 0.008),
    }).setOrigin(0.5)
    const icon = this.textures.exists('item_revive')
      ? this.add.image(cx, cy - ph * 0.05, 'item_revive').setDisplaySize(width * 0.24, width * 0.24)
      : this.add.text(cx, cy - ph * 0.05, '❤️', { fontSize: `${Math.round(width * 0.16)}px` }).setOrigin(0.5)
    const count = this.add.text(cx, cy + ph * 0.18, `보유 ${getItemCount('revive')}`, {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.044)}px`, color: '#ffe066', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5)
    c.add([dim, g, title, icon, count])

    const close = () => c.destroy()
    makeButton(this, cx - pw * 0.24, cy + ph * 0.37, '사용', () => {
      close()
      if (useShopItem('revive')) {
        this.state = { ...this.state, status: 'playing', timeRemaining: START_TIME, selectedIds: [] }
        this.board.render(this.state)
        this.events.emit('state', this.state)
        this.startTimer()
        this.events.emit('sfx', 'time_bonus')
        this.showToast('❤️ 부활!')
      } else {
        this.finishGameOver()
      }
    }, { parent: c, width: pw * 0.42, height: width * 0.14, fontSize: width * 0.05, color: 0xff7a7a, pressColor: 0xe86060 })
    makeButton(this, cx + pw * 0.24, cy + ph * 0.37, '안함', () => { close(); this.finishGameOver() },
      { parent: c, width: pw * 0.42, height: width * 0.14, fontSize: width * 0.05, color: 0xa98bff, pressColor: 0x8f6fe8 })
  }

  private async animateCleared(ids: number[]) {
    this.shakeNeighbors(ids)
    await Promise.all(ids.map(id => this.board.spriteOf(id)?.popOut() ?? Promise.resolve()))
    if (!this.paused) this.board.render(this.state) // 일시정지 중이면 복원하지 않음(블록 노출 방지)
  }

  /** 터진 블록의 인접(8방향) 생존 블록을 바깥으로 튕겨 물리 반응 연출. */
  private shakeNeighbors(clearedIds: number[]) {
    const cleared = new Set(clearedIds)
    const posMap = new Map<string, number>() // "x,y" -> block id (생존 블록만)
    for (const b of this.state.blocks) {
      if (b.value === 0 || cleared.has(b.id)) continue
      posMap.set(`${b.x},${b.y}`, b.id)
    }
    const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]]
    const nudged = new Set<number>()
    for (const cb of this.state.blocks) {
      if (!cleared.has(cb.id)) continue
      for (const [dx, dy] of dirs) {
        const id = posMap.get(`${cb.x + dx},${cb.y + dy}`)
        if (id === undefined || nudged.has(id)) continue
        nudged.add(id)
        this.board.spriteOf(id)?.nudge(dx, dy)
      }
    }
  }

  /** 연습 힌트: 합이 10이 되는 블록 후보를 찾아 펄스로 강조. */
  private showHint() {
    if (this.paused || this.state.status !== 'playing') return
    const ids = findHint(this.state.blocks, this.state.boardSize)
    if (!ids || ids.length === 0) return
    AudioManager.play('block_select', 0.5)
    for (const id of ids) this.board.spriteOf(id)?.pulseHint()
  }

  /** 💡 힌트: 무료 획득분 우선, 없으면 구매분을 소모하고 합10 조합 강조. */
  private useHintItemRow() {
    if (this.paused || this.state.status !== 'playing') return
    if (useHintItem()) {
      // 무료 획득 힌트 1개 소모
    } else if (!useShopItem('hint')) {
      this.showToast('아이템이 없어요')
      return
    }
    this.showHint()
    this.itemCountTexts['hint']?.setText(`${getHintItems() + getItemCount('hint')}`)
  }

  /** ⏰ 시간: 보유 시 게임 중 +15초(소모). */
  private useTimeItem() {
    if (this.paused || this.state.status !== 'playing') return
    if (!useShopItem('time')) { this.showToast('아이템이 없어요'); return }
    this.state = { ...this.state, timeRemaining: this.state.timeRemaining + 15 }
    this.events.emit('state', this.state)
    this.itemCountTexts['time']?.setText(`${getItemCount('time')}`)
    this.events.emit('sfx', 'time_bonus')
    this.showToast('⏰ +15초')
  }

  /** 🔀 셔플: 블럭이 남아 있는 칸끼리만 값을 무작위로 섞어 재배치. 빈(클리어된) 칸은 그대로, 새 블럭 생성 없음. */
  private useShuffleItem() {
    if (this.paused || this.state.status !== 'playing') return
    if (!useShopItem('shuffle')) { this.showToast('아이템이 없어요'); return }
    // 남은 블럭(value≠0)을 보드 전체의 무작위 칸으로 재배치(새 블럭 추가 없음).
    // 합10 조합이 1개 이상 나오는 배치가 될 때까지 재시도(최대 40회).
    const values = this.state.blocks.filter(b => b.value !== 0).map(b => b.value)
    const total = this.state.blocks.length
    let candidate = this.state.blocks
    for (let attempt = 0; attempt < 40; attempt++) {
      const rng = makeRng(this.nextSeed())
      const order = Array.from({ length: total }, (_, i) => i)
      for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); const t = order[i]; order[i] = order[j]; order[j] = t }
      const newVals = new Array(total).fill(0)
      for (let k = 0; k < values.length; k++) newVals[order[k]] = values[k]
      candidate = this.state.blocks.map((b, i) => ({ ...b, value: newVals[i] }))
      if (findHint(candidate, this.state.boardSize)) break // 합10 조합 보장되면 채택
    }
    this.state = { ...this.state, blocks: candidate, selectedIds: [] }
    // 스프라이트를 새 배치로 재생성: 클리어로 scale0/alpha0 된 칸의 블럭도 정상 복원(안 보이는 블럭 버그 방지).
    this.board.layout(this.state)
    this.board.render(this.state)
    this.itemCountTexts['shuffle']?.setText(`${getItemCount('shuffle')}`)
    this.events.emit('sfx', 'combo')
    this.showToast('🔀 블럭을 재배치했어요')
  }

  /** 💣 폭탄: 남은 블럭을 모두 터뜨리고 다음 스테이지로 넘김(점수 가산 없음 = stageClear 시퀀스 재사용). */
  private useBombItem() {
    if (this.paused || this.state.status !== 'playing') return
    if (!useShopItem('bomb')) { this.showToast('아이템이 없어요'); return }
    const remainingIds = this.state.blocks.filter(b => b.value !== 0).map(b => b.id)
    this.itemCountTexts['bomb']?.setText(`${getItemCount('bomb')}`)
    this.timerEvent?.remove()
    // 전환 중 상태로(타이머/입력 차단). 점수는 더하지 않음.
    this.state = { ...this.state, status: 'stageCompleting', selectedIds: [] }
    this.events.emit('state', this.state)
    this.events.emit('sfx', 'stage_clear')
    this.showToast('💣 폭탄! 다음 스테이지로')
    void this.runStageClearSequence([], remainingIds)
  }

  /** 🔨 망치 모드 진입: 다음에 탭한 블록을 제거. */
  private enterHammerMode() {
    if (this.paused || this.state.status !== 'playing') return
    if (getItemCount('hammer') <= 0) { this.showToast('아이템이 없어요'); return }
    this.hammerMode = true
    this.showToast('🔨 제거할 블록을 탭하세요')
  }

  private exitHammerMode() { this.hammerMode = false }

  private useHammerAt(p: Phaser.Input.Pointer) {
    this.exitHammerMode()
    const ids = this.board.idsInRect({ x: p.x - 2, y: p.y - 2, w: 4, h: 4 }, this.state)
    const id = ids.find(i => { const b = this.state.blocks.find(x => x.id === i); return !!b && b.value !== 0 })
    if (id === undefined) return // 빈 곳/바깥 탭 → 소모하지 않음
    if (!useShopItem('hammer')) { this.showToast('아이템이 없어요'); return }
    // 제거 위치에서 터지는 이펙트(물방울 파편)
    const sp = this.board.spriteOf(id)
    const bx = sp?.x ?? p.x, by = sp?.y ?? p.y
    this.state = { ...this.state, blocks: this.state.blocks.map(b => b.id === id ? { ...b, value: 0 } : b), selectedIds: [] }
    this.board.render(this.state)
    this.shatter.burst(bx, by, 0xeaf6ff, 1.4)
    this.events.emit('sfx', 'block_clear')
    this.itemCountTexts['hammer']?.setText(`${getItemCount('hammer')}`)
  }

  // ── 일시정지 / 나가기 ──────────────────────────────────────────
  /** 보드를 가리고 어두운 오버레이 패널을 띄운다(공통). 콘텐츠 빌더를 받는다. */
  private openOverlay(build: (cx: number, cy: number, panel: Phaser.GameObjects.Container) => void) {
    if (this.paused) return
    this.paused = true
    this.timerEvent?.remove()
    this.board.concealAll() // 블록 숫자 가리기(어뷰징 방지: 멈춰서 다음 수 분석 불가)
    const { width, height } = this.scale
    // dim은 패널의 첫 자식(맨 아래). 패널 자체를 높은 depth로 올려 보드/HUD 위에 표시.
    // 거의 불투명하게 덮어 혹시 모를 한 프레임 노출도 차단한다.
    const dim = this.add.rectangle(width / 2, height / 2, width, height, 0x10082a, 1)
    const panel = this.add.container(0, 0).setDepth(50)
    panel.add(dim)
    build(width / 2, height / 2, panel)
    this.overlay = panel
  }

  private closeOverlay() {
    this.overlay?.destroy()
    this.overlay = undefined
    this.paused = false
  }

  private pauseGame() {
    this.openOverlay((cx, cy, panel) => {
      const { width } = this.scale
      panel.add(this.add.text(cx, cy - width * 0.22, '일시정지', {
        fontFamily: 'Arial', fontSize: `${Math.round(width * 0.1)}px`, color: '#ffffff', fontStyle: 'bold',
        stroke: '#3a2a6a', strokeThickness: Math.round(width * 0.012),
      }).setOrigin(0.5))
      makeButton(this, cx, cy, '계속하기', () => this.resumeGame(), { parent: panel })
      makeButton(this, cx, cy + width * 0.18, '나가기', () => this.confirmExitFromPause(),
        { color: 0xff8a8a, pressColor: 0xe87070, parent: panel })
    })
  }

  private resumeGame() {
    this.closeOverlay()
    this.board.render(this.state) // 블록 다시 표시
    this.startTimer()
  }

  private confirmExit() {
    this.openOverlay((cx, cy, panel) => this.buildExitDialog(cx, cy, panel))
  }

  /** 일시정지 화면에서 '나가기'를 누른 경우: 기존 오버레이를 종료 다이얼로그로 교체 */
  private confirmExitFromPause() {
    this.closeOverlay()
    this.confirmExit()
  }

  private buildExitDialog(cx: number, cy: number, panel: Phaser.GameObjects.Container) {
    const { width } = this.scale
    panel.add(this.add.text(cx, cy - width * 0.2, '정말 나가시겠어요?', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.06)}px`, color: '#ffffff', fontStyle: 'bold',
      stroke: '#3a2a6a', strokeThickness: Math.round(width * 0.01), align: 'center',
    }).setOrigin(0.5))
    panel.add(this.add.text(cx, cy - width * 0.1, '진행 중인 게임은 저장되지 않아요', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.04)}px`, color: '#ffd9d9',
    }).setOrigin(0.5))
    makeButton(this, cx, cy + width * 0.04, '예, 나갈게요', () => this.leaveToLobby(),
      { color: 0xff8a8a, pressColor: 0xe87070, parent: panel })
    makeButton(this, cx, cy + width * 0.22, '아니오', () => this.resumeGame(),
      { color: 0x66c2ff, pressColor: 0x4aa8e8, parent: panel })
  }

  private leaveToLobby() {
    this.closeOverlay()
    this.timerEvent?.remove()
    this.scene.stop('UI')
    this.scene.start('Lobby')
  }

  /** 보드 위 미니 음악 플레이어: 이전 / 재생·정지 / 다음 + 현재 곡명(작은 글씨). */
  private buildMiniPlayer(boardCy: number, boardPx: number) {
    const { width } = this.scale
    const rowY = boardCy - boardPx / 2 - width * 0.048
    const r = width * 0.022
    const gap = width * 0.068

    // 한 줄: [이전][재생·정지][다음]  곡명(오른쪽, 마퀴)
    const prevX = width * 0.16
    const playX = prevX + gap
    const nextX = playX + gap
    this.addMediaBtn(prevX, rowY, r, 'prev', () => { AudioManager.play('ui_click', 0.3); AudioManager.prevTrack(); this.refreshMiniPlayer() })
    this.playPauseRedraw = this.addMediaBtn(playX, rowY, r * 1.05, 'play', () => {
      AudioManager.play('ui_click', 0.3); AudioManager.setBgm(!AudioManager.isBgmOn()); this.refreshMiniPlayer()
    })
    this.addMediaBtn(nextX, rowY, r, 'next', () => { AudioManager.play('ui_click', 0.3); AudioManager.nextTrack(); this.refreshMiniPlayer() })

    // 곡명: 마스크 창 안에서 오른쪽→왼쪽 슬라이드(일정 간격 반복)
    this.nameLeft = nextX + r + width * 0.035
    this.nameRight = width * 0.9
    this.playerName = this.add.text(this.nameLeft, rowY, '', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.026)}px`, color: '#ffffff', fontStyle: 'bold',
      stroke: '#3a2a6a', strokeThickness: Math.max(2, Math.round(width * 0.005)),
    }).setOrigin(0, 0.5).setDepth(26)
    const maskShape = this.make.graphics()
    maskShape.fillStyle(0xffffff)
    maskShape.fillRect(this.nameLeft, rowY - r * 1.6, this.nameRight - this.nameLeft, r * 3.2)
    this.playerName.setMask(maskShape.createGeometryMask())

    this.refreshMiniPlayer()
    // 곡명/재생 상태 주기 갱신(랜덤 진행·정시 전환·로드 완료 반영)
    this.time.addEvent({ delay: 1000, loop: true, callback: () => this.refreshMiniPlayer() })
  }

  /** 원형 버튼 + 벡터 아이콘(prev/play/pause/next). 반환: 아이콘 다시 그리기 함수. */
  private addMediaBtn(cx: number, cy: number, r: number, kind: 'prev' | 'play' | 'next', onClick: () => void): () => void {
    const { width } = this.scale
    const ic = this.add.graphics().setDepth(26)
    const draw = () => {
      ic.clear()
      ic.fillStyle(0xffffff, 1)
      ic.lineStyle(Math.max(1, width * 0.003), 0x3a2a6a, 1)
      const bh = r * 1.4
      const k = kind === 'play' ? (AudioManager.isBgmOn() ? 'pause' : 'play') : kind
      if (k === 'play') {
        ic.fillTriangle(cx - r * 0.45, cy - r * 0.7, cx - r * 0.45, cy + r * 0.7, cx + r * 0.8, cy)
        ic.strokeTriangle(cx - r * 0.45, cy - r * 0.7, cx - r * 0.45, cy + r * 0.7, cx + r * 0.8, cy)
      } else if (k === 'pause') {
        ic.fillRect(cx - r * 0.5, cy - bh / 2, r * 0.34, bh); ic.strokeRect(cx - r * 0.5, cy - bh / 2, r * 0.34, bh)
        ic.fillRect(cx + r * 0.16, cy - bh / 2, r * 0.34, bh); ic.strokeRect(cx + r * 0.16, cy - bh / 2, r * 0.34, bh)
      } else if (k === 'prev') {
        ic.fillRect(cx - r * 0.75, cy - bh / 2, r * 0.3, bh); ic.strokeRect(cx - r * 0.75, cy - bh / 2, r * 0.3, bh)
        ic.fillTriangle(cx + r * 0.6, cy - r * 0.7, cx + r * 0.6, cy + r * 0.7, cx - r * 0.35, cy)
        ic.strokeTriangle(cx + r * 0.6, cy - r * 0.7, cx + r * 0.6, cy + r * 0.7, cx - r * 0.35, cy)
      } else { // next
        ic.fillTriangle(cx - r * 0.6, cy - r * 0.7, cx - r * 0.6, cy + r * 0.7, cx + r * 0.35, cy)
        ic.strokeTriangle(cx - r * 0.6, cy - r * 0.7, cx - r * 0.6, cy + r * 0.7, cx + r * 0.35, cy)
        ic.fillRect(cx + r * 0.45, cy - bh / 2, r * 0.3, bh); ic.strokeRect(cx + r * 0.45, cy - bh / 2, r * 0.3, bh)
      }
    }
    draw()
    this.add.zone(cx, cy, r * 2.6, r * 2.6).setInteractive({ useHandCursor: true }).setDepth(27).on('pointerup', onClick)
    return draw
  }

  private refreshMiniPlayer() {
    this.playPauseRedraw?.()
    const name = AudioManager.getCurrentTrackName()
    if (name === this.currentTrackName) return // 곡 안 바뀌면 마퀴 유지
    this.currentTrackName = name
    this.startMarquee(name)
  }

  /** 평소엔 왼쪽에 정지, 약 20초마다 오른쪽에서 천천히 슬라이드되어 다시 정착. */
  private startMarquee(name: string) {
    if (!this.playerName) return
    this.marqueeTween?.stop()
    this.marqueeTimer?.remove()
    this.playerName.setText(name).setX(this.nameLeft) // 평소: 왼쪽 정착(정지)
    this.marqueeTimer = this.time.addEvent({
      delay: 20000, loop: true,
      callback: () => {
        if (!this.playerName) return
        this.marqueeTween?.stop()
        this.playerName.setX(this.nameRight)
        this.marqueeTween = this.tweens.add({
          targets: this.playerName, x: this.nameLeft, duration: 4200, ease: 'Sine.InOut',
        })
      },
    })
  }
}
